import packageJson from "../../package.json";

/** npm package name (used to load the standalone circuits module). */
export const PACKAGE_NAME = packageJson.name;

/** Published npm version (pins CDN circuit URLs in production). */
export const PACKAGE_VERSION = packageJson.version;

/** Withdraw circuit artifact filenames (published under `arcane-sdk/circuits/`). */
export const WITHDRAW_CIRCUIT_WASM = "withdraw.wasm";
export const WITHDRAW_CIRCUIT_ZKEY = "withdraw.zkey";
/**
 * Pinned SHA-256 hashes for the published withdraw circuit artifacts.
 * These are verified in-browser before passing artifacts to snarkjs.
 */
export const WITHDRAW_CIRCUIT_SHA256 = {
  wasm: "2565a8dd94b0b3cb9ec6d415933ab05ed5d32ac07165b71be6bcf31615fc0330",
  zkey: "64a4d915099f6fe6bfa06e151c254843be8773c7991c742260019dc5184e5d82",
} as const;

/** Optional self-hosted path convention (opt-in via `circuitPathsBase`). */
export const SELF_HOSTED_CIRCUIT_BASE = "/circuits/";

/**
 * Stable published source for the pinned circuit artifacts.
 *
 * Decoupled from {@link PACKAGE_VERSION} on purpose: the withdraw circuit is a
 * fixed cryptographic artifact, so SDK code releases must not change where the
 * verified circuits are fetched from. Bump these together with
 * {@link WITHDRAW_CIRCUIT_SHA256} only when the circuit itself changes.
 */
export const CIRCUIT_CDN_PACKAGE = "arcane-privacy-sdk";
export const CIRCUIT_CDN_VERSION = "0.0.2";

export type CircuitPaths = {
  wasm: string;
  zkey: string;
};

/** Resolve circuit URLs when the caller knows the package `dist/` root. */
export function resolveCircuitPathsFromBase(base: string): CircuitPaths {
  const root = base.endsWith("/") ? base : `${base}/`;
  return {
    wasm: `${root}circuits/${WITHDRAW_CIRCUIT_WASM}`,
    zkey: `${root}circuits/${WITHDRAW_CIRCUIT_ZKEY}`,
  };
}

/**
 * Base URL for circuit files on jsDelivr.
 *
 * Pinned to {@link CIRCUIT_CDN_PACKAGE}@{@link CIRCUIT_CDN_VERSION} (not the
 * current SDK version) so the bytes always match {@link WITHDRAW_CIRCUIT_SHA256}.
 * This URL is independent of the consuming app's origin/domain.
 */
export function resolveNpmCdnCircuitBase(): string {
  return `https://cdn.jsdelivr.net/npm/${CIRCUIT_CDN_PACKAGE}@${CIRCUIT_CDN_VERSION}/dist/circuits/`;
}

/** Trusted jsDelivr fallback paths for pinned withdraw circuit artifacts. */
export function resolveNpmCdnCircuitPaths(): CircuitPaths {
  const base = resolveNpmCdnCircuitBase();
  return {
    wasm: `${base}${WITHDRAW_CIRCUIT_WASM}`,
    zkey: `${base}${WITHDRAW_CIRCUIT_ZKEY}`,
  };
}

function isLocalDevHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  );
}

/**
 * Browser circuit URLs.
 *
 * - Local dev (Vite): `/node_modules/<pkg>/dist/circuits/` — Vite serves node_modules.
 * - Production: pinned, domain-independent jsDelivr CDN (verified by SHA-256 before use).
 *
 * The production default does not depend on the app's own origin/domain, so it
 * works on any deployed host with no copy step. Artifacts are always integrity
 * checked before proving (see {@link WITHDRAW_CIRCUIT_SHA256}). To self-host
 * instead, pass {@link ArcaneSdkInitOptions.circuitPathsBase} (e.g. `"/circuits/"`);
 * it is verified too and falls back to the CDN if unavailable.
 */
export function resolveBrowserCircuitPaths(
  baseOverride?: string
): CircuitPaths | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (baseOverride !== undefined) {
    return resolveCircuitPathsFromBase(baseOverride);
  }

  if (isLocalDevHost(window.location.hostname)) {
    const base = `/node_modules/${PACKAGE_NAME}/dist/circuits/`;
    return {
      wasm: `${base}${WITHDRAW_CIRCUIT_WASM}`,
      zkey: `${base}${WITHDRAW_CIRCUIT_ZKEY}`,
    };
  }

  return resolveNpmCdnCircuitPaths();
}
